import React, { useContext, useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";

// Services
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import CountryContext from "../CountryContext";
import apiRequest from "../services/apiRequest";
import { NotificationsContext } from "../ui-components/Notifications";
import AuthenticationService from "../services/AuthenticationService";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";

// Components
import ReferenceContent from "../components/ReferenceContent";
import ReferenceApproval from "../components/ReferenceApproval";

// UI
import Alert from "../ui-components/Alert";
import Modal from "../ui-components/Modal";
import Button from "../ui-components/Button";
import Placeholder from "../ui-components/Placeholder";
import StatusReference from "../components/StatusReference";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { PageHeading, PageSection } from "../ui-components/Container";
import { MapPinIcon } from "@heroicons/react/24/solid";
import NotFound from "../ui-components/pages/NotFound";
import { checkIfReferenceClosed } from "../utils/worksiteReferencesUtils";
import { WORKSITE_REFERENCES_SECTIONS } from "../utils/constants";
import ReferencePendingApproval from "../components/ReferencePendingApproval";
import { getPDF } from "../utils";
import moment from "moment";
import UserNotificationsContext from "../UserNotificationsContext";

const WorksiteReference = () => {
  const intl = useIntl();
  const history = useHistory();

  const userData = AuthenticationService.getUserData();
  const userScopes = AuthenticationService.getUserScopes();
  const { worksite_id, reference_id } = useParams();

  const { country } = useContext(CountryContext);
  const countryApiRequest = useCountryAxiosInstance();

  const { userNotifications, setUserNotifications } = useContext(
    UserNotificationsContext
  );
  const { push } = useContext(NotificationsContext);

  const [loading, setLoading] = useState(true);
  const [reference, setReference] = useState();
  const [worksite, setWorksite] = useState();
  const [approvals, setApprovals] = useState();
  const [linkedUsers, setLinkedUsers] = useState();
  const [isClosed, setIsClosed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [approvalConfirm, setApprovalConfirm] = useState();
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);

  useEffect(() => {
    setIsClosed(checkIfReferenceClosed(approvals));
  }, [approvals]);

  const fetchLinkedUsers = async (r_perm) => {
    const users = await apiRequest.post("/users/from_perm_list", {perm: r_perm});
    setLinkedUsers(users);
    return users;
  };

  useEffect(() => {
    // Hook to be executed on page load. Handles two distinct scenarios:
    // A) Existing reference (`reference_id` is defined): in this case, it tries to retrieve the reference and its 
    //    approvals. If the reference is not found or not active, redirects to the 'new reference' page
    // B) New reference (`reference_id` is not defined): in this case, it downloads the worksite (which is always
    //    needed, either to display the reference or to create a new one) and linked users, but does not create any 
    //    reference yet

    if (!!reference_id) {
      // Scenario A: existing reference - try to retrieve it
      const fetchReference = async () => {
        if (!reference_id) return null;

        let redirectToNewReferencePage = false;
        try {
          const results = await countryApiRequest.get(
            `/worksites_references/all`,
            {params: {ids: reference_id, join: "worksite"}}
          );
          if (results.length !== 1)
            throw new Error("Reference not found");
          const reference = results[0];
          if (reference.flg_active) {
            setReference(_.omit(reference, ["worksite"]));
            setWorksite(reference.worksite);
            const approvals = await countryApiRequest.get(
              `/worksites_references_approvals/all?cod_reference_id=${reference.id}`
            );
            setApprovals(approvals);
          } else redirectToNewReferencePage = true;
        } catch {
          redirectToNewReferencePage = true;
        }

        if (redirectToNewReferencePage)
          history.replace({
            pathname: `/${country.code}/worksites/${worksite_id}/reference`,
          });

        return reference;
      };
      // retrieve reference
      fetchReference().then((r) => {
        setLoading(false);
      });
    } else {
      // Scenario B: new reference - retrieve worksite and linked users, but do not create any reference yet
      countryApiRequest
        .get(`/worksites/${worksite_id}`)
        .then(setWorksite)
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setLoading(false);
        })
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if the reference has never been seen by the current user, delete the 'new reference' notification
    const newReferenceNotification = userNotifications?.find(
      (n) =>
        ["worksite_reference", "worksite_reference_report"].includes(
          n.val_type
        ) && n.cod_reference === parseInt(reference_id)
    );
    if (newReferenceNotification)
      setUserNotifications((prev) =>
        prev.filter((n) => n.id !== newReferenceNotification.id)
      );
  }, [userNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (worksite && !linkedUsers) {
      fetchLinkedUsers(worksite.r_perm);
    }
  }, [worksite, linkedUsers]);

  const createReference = async () => {
    if (!worksite || reference) return;

    try {
      const result = await countryApiRequest.post("/worksites_references", {
        cod_worksite_id: worksite.id,
        flg_active: true,
        r_perm: ["admin", "field", `user_${userData.id}`],
        w_perm: ["admin", "field", "CS", `user_${userData.id}`],
        creator_email: userData.email,
      });
      setReference(result);
      push({
        type: "success",
        title: intl.formatMessage({ id: "reference_created_successfully" }),
      });
      history.replace({
        pathname: `/${country.code}/worksites/${worksite_id}/reference/${result.id}`,
      });
    } catch {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
    }
  };

  const deleteCurrentReference = async () => {
    if (!reference) return;

    try {
      await countryApiRequest.put(`/worksites_references/${reference.id}`, {
        ...reference,
        flg_active: false,
      });
      setReference(undefined);
      setApprovals(undefined);
      push({
        type: "info",
        title: intl.formatMessage({ id: "reference_removed_successfully" }),
      });
      history.replace({
        pathname: `/${country.code}/worksites/${worksite_id}/reference`,
      });
    } catch (error) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
      throw error;
    }
  };

  const currentUserPendingApproval = approvals?.find(
    (a) =>
      a.cod_approved_by === userData.id &&
      a.tms_sent_request_on &&
      !a.tms_approved_on
  );

  const getApprovalRequestContent = () => {
    if (!currentUserPendingApproval?.des_approval_type) return null;

    let approvalTypeIntlKey = currentUserPendingApproval.des_approval_type;
    if (approvalTypeIntlKey === "marketing")
      approvalTypeIntlKey = "ho_external_communication";

    return (
      <>
        <span className="my-2">
          {intl.formatMessage(
            { id: "approval_request_description" },
            {
              role: (
                <span className="font-semibold">
                  {intl
                    .formatMessage({ id: approvalTypeIntlKey })
                    .toLowerCase()}
                </span>
              ),
            }
          )}
          .
        </span>
        <br />
        <span>
          {intl.formatMessage({
            id: `approval_request_description_ext_${currentUserPendingApproval.des_approval_type}`,
          })}
        </span>
      </>
    );
  };

  const downloadReferencePDF = () => {
    const body = {
      id_reference: reference_id,
      edited_at: reference.edited_at,
      last_download: reference.last_download,
      isClosed: isClosed,
      content: reference.content,
      images: reference.images_urls ?? [],
      summary: reference.technical_sheet
        ? {
            ...reference.technical_sheet,
            products: reference.technical_sheet.products?.map((entry) =>
              typeof entry === "object"
                ? { system: entry.title, system_products: entry.items }
                : { single_product: entry }
            ),
          }
        : null,
    };
    setDownloading(true);
    getPDF(
      intl,
      push,
      countryApiRequest,
      "/reference",
      body,
      `${reference.content.title}_${moment().format("YYYYMMDDhhmmss")}.pdf`,
      () => {},
      () => {},
      true
    ).finally(() => {
      setDownloading(false);
    });
  };

  // ---------- RENDER
  if (!loading && !worksite)
    return (
      <div className="h-screen flex items-center justify-center">
        <NotFound />
      </div>
    );
  return (
    <>
      <PageHeading
        title={intl.formatMessage({ id: "reference" })}
        historyBackPath={`/${country.code}/worksites/${worksite_id}`}
        contentActions={
          <>
            <div className="flex items-center gap-x-2 text-lg">
              <MapPinIcon className="w-5 h-5" />
              {worksite ? (
                <span>
                  {`${worksite.address} - ${worksite.city}`.toUpperCase()}
                </span>
              ) : (
                <Placeholder width="w-44" classNames="mt-2" />
              )}
            </div>
          </>
        }
      />
      <div className="border-am-700 border-t mb-10" />
      <div className="grid grid-cols-12 gap-x-8">
        <div className="col-span-12 order-last md:col-span-8 md:order-first">
          <PageSection title={intl.formatMessage({ id: "summary" })}>
            {currentUserPendingApproval && (
              <div className="mb-5">
                <Alert
                  title={intl.formatMessage({ id: "approval_request" })}
                  text={getApprovalRequestContent()}
                  onConfirm={() => {
                    setApprovalConfirm(currentUserPendingApproval);
                  }}
                  confirmText={intl
                    .formatMessage({
                      id:
                        currentUserPendingApproval.des_approval_type ===
                        "progettista"
                          ? "sign_liberatoria"
                          : "proceed",
                    })
                    .toUpperCase()}
                />
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-y-3">
              <div className="flex gap-2">
                <span className="mr-1">
                  {intl.formatMessage({ id: "state" })}:
                </span>
                <span
                  className={
                    loading || !reference
                      ? "text-gray-300"
                      : isClosed
                      ? `text-${userData.business}-400`
                      : "text-yellow-300"
                  }
                >
                  &#x25cf;
                </span>
                <span className="font-bold">
                  {loading ? (
                    <Placeholder width="w-32" />
                  ) : (
                    intl.formatMessage({
                      id: reference
                        ? isClosed
                          ? "closed_f"
                          : "in_progress_creation"
                        : "not_available",
                    })
                  )}
                </span>
              </div>
              <div className="flex gap-2">
                {!loading && (
                  <>
                    {reference && (
                      <Button
                        disabled={
                          !reference ||
                          !Object.entries(WORKSITE_REFERENCES_SECTIONS)
                            .filter(([_, info]) => info.required === true)
                            .every(([field, _]) => !!reference.content?.[field])
                        }
                        onClick={downloadReferencePDF}
                        submitting={downloading}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        <span className="mx-1">
                          {intl.formatMessage({
                            id: isClosed ? "download" : "preview",
                          })}
                        </span>
                      </Button>
                    )}
                    {!reference && userScopes.includes("reference:create") && (
                      <Button
                        styleType="green"
                        onClick={() => {
                          createReference();
                        }}
                      >
                        {intl.formatMessage({ id: "create_reference" })}
                      </Button>
                    )}
                    {reference && userScopes.includes("reference:delete") && (
                      <Button
                        styleType="greenWhite"
                        onClick={() => {
                          setIsConfirmDeleteModalOpen(true);
                        }}
                      >
                        {intl.formatMessage({ id: "delete" })}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </PageSection>
          {reference && linkedUsers && (
            <>
              <ReferenceApproval
                intl={intl}
                push={push}
                reference={reference}
                approvals={approvals}
                setApprovals={setApprovals}
                linkedUsers={linkedUsers}
                setLinkedUsers={setLinkedUsers}
                worksite_id={worksite_id}
                worksite_name={worksite.des_worksite}
                countryApiRequest={countryApiRequest}
                editable={!isClosed && userScopes.includes("reference:update")}
              />
              <ReferenceContent
                intl={intl}
                push={push}
                worksite={worksite}
                reference={reference}
                setReference={setReference}
                countryApiRequest={countryApiRequest}
                editable={
                  (!isClosed || userData.role_type === "admin") &&
                  userScopes.includes("reference:update")
                }
              />
            </>
          )}
        </div>
        <div className="col-span-12 order-first md:col-span-4 md:order-last">
          <PageSection title={intl.formatMessage({ id: "progress" })}>
            <StatusReference
              className="px-4 pt-1"
              {...{ reference, approvals }}
            />
          </PageSection>
        </div>
      </div>
      {isConfirmDeleteModalOpen && (
        <Modal
          title={intl.formatMessage({ id: "confirm_delete" })}
          description={intl.formatMessage({
            id: "confirm_reference_delete_dialog",
          })}
          onExit={() => {
            setIsConfirmDeleteModalOpen(false);
          }}
          onDismiss={() => {
            setIsConfirmDeleteModalOpen(false);
          }}
          onConfirm={() => {
            deleteCurrentReference();
          }}
          dismissText={intl.formatMessage({ id: "cancel" })}
          confirmText={intl.formatMessage({ id: "confirm" })}
        />
      )}
      {approvalConfirm && currentUserPendingApproval && (
        <ReferencePendingApproval
          intl={intl}
          push={push}
          setApprovalConfirm={setApprovalConfirm}
          currentUserPendingApproval={currentUserPendingApproval}
          countryApiRequest={countryApiRequest}
          approvals={approvals}
          setApprovals={setApprovals}
          reference={reference}
          worksite={worksite}
        />
      )}
    </>
  );
};

export default WorksiteReference;
